import React, { useState, useMemo } from 'react';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import NavigationLinksHr from './NavLinkHr';

const attendanceData = [
  { date: '2025-07-18', present: ['Rahim', 'Salma'], absent: ['Nasim'] },
  { date: '2025-07-17', present: ['Nasim'], absent: ['Rahim', 'Salma'] },
  { date: '2025-07-16', present: ['Rahim'], absent: ['Nasim', 'Salma'] },
  // Add more records to test pagination
];

const recordsPerPage = 5;

function AttendanceSheet() {
  const [filter, setFilter] = useState('All');
  const [month, setMonth] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const filteredData = useMemo(() => attendanceData
    .filter((entry) => (month ? entry.date.startsWith(month) : true))
    .map((entry) => {
      const present = entry.present.filter((name) => name.toLowerCase()
        .includes(search.toLowerCase()));
      const absent = entry.absent.filter((name) => name.toLowerCase()
        .includes(search.toLowerCase()));

      return { ...entry, present, absent };
    })
    .filter((entry) => {
      if (filter === 'All') return true;
      if (filter === 'Present') return entry.present.length > 0;
      if (filter === 'Absent') return entry.absent.length > 0;
      return true;
    }), [filter, month, search]);

  const paginatedData = filteredData.slice(
    (page - 1) * recordsPerPage,
    page * recordsPerPage,
  );

  const totalPages = Math.ceil(filteredData.length / recordsPerPage);

  const exportCSV = () => {
    const rows = [];

    filteredData.forEach(({ date, present, absent }) => {
      present.forEach((name) => rows.push({ Date: date, Name: name, Status: 'Present' }));
      absent.forEach((name) => rows.push({ Date: date, Name: name, Status: 'Absent' }));
    });

    const csv = Papa.unparse(rows);
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'attendance.csv');
  };

  return (
    <>
      <div className="p-6 space-y-6">
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 py-2">
          <NavigationLinksHr />
        </div>

        <h2 className="text-2xl font-semibold">Attendance Sheet</h2>

        <div className="flex flex-wrap gap-2">
          <input
            type="month"
            className="border rounded px-2 py-1"
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              setPage(1);
            }}
          />
          <input
            type="text"
            className="border rounded px-2 py-1"
            placeholder="Search name"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
          <button
            type="button"
            onClick={exportCSV}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="flex gap-2 my-2">
        {['All', 'Present', 'Absent'].map((status) => (
          <button
            type="button"
            key={status}
            className={`px-3 py-1 rounded border ${
              filter === status
                ? 'bg-gray-800 text-white'
                : 'bg-white text-gray-700'
            }`}
            onClick={() => {
              setFilter(status);
              setPage(1);
            }}
          >
            {status}
          </button>
        ))}
      </div>

      {paginatedData.length === 0 && (
        <p className="text-gray-500 text-center mt-4">No attendance records found.</p>
      )}

      {paginatedData.map((entry) => (
        <div key={entry.date} className="bg-white border rounded-xl p-4 shadow">
          <p className="font-semibold mb-2">
            📅 Date:
            {entry.date}
          </p>
          {entry.present.length > 0 && (
            <p className="text-sm text-green-700">
              ✅ Present:
              {' '}
              {entry.present.join(', ')}
            </p>
          )}
          {entry.absent.length > 0 && (
            <p className="text-sm text-red-600">
              ❌ Absent:
              {' '}
              {entry.absent.join(', ')}
            </p>
          )}
        </div>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <button
            type="button"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              type="button"
              key={p}
              onClick={() => setPage(p)}
              className={`px-3 py-1 border rounded ${
                p === page ? 'bg-gray-800 text-white' : ''
              }`}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </>
  );
}

export default AttendanceSheet;
